// Sentry initialization should be imported first!
import "./instrument";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/common.css";
import "./styles/scrollbars.css";
import "./styles/collaboration.css";
import "tippy.js/dist/tippy.css";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import * as Sentry from "@sentry/react";
import { TRPCClientError } from "@trpc/client";
import { Portal } from "radix-ui";
import App from "./App.tsx";
import { CookieConsentBanner } from "./components/CookieConsent.tsx";
import { Toaster } from "./components/ui/sonner.tsx";
import { initAnalytics } from "./lib/analytics.ts";

// Make version available globally (injected by Vite at build time)
window.APP_VERSION = __APP_VERSION__;
console.log(`Orsane version: ${window.APP_VERSION}`);

// Initialize Google Tag Manager (used for ads conversion tracking)
initAnalytics();

// Register service worker for PWA
if (import.meta.env.ENABLE_SERVICE_WORKER !== "true") {
    console.log("Service worker is disabled in the environment");
}
if (import.meta.env.ENABLE_SERVICE_WORKER === "true" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register("/sw.js")
            .then((registration) => {
                console.log("SW registered: ", registration);
            })
            .catch((registrationError) => {
                console.log("SW registration failed: ", registrationError);
            });
    });
}

const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
    console.log("SENTRY_DSN found, initializing Sentry");
}

// A NOT_FOUND caught by an ErrorBoundary is expected: every boundary renders a "not found"
// fallback for it (e.g. an email link to a task in another workspace than the active one).
const isHandledNotFound = (error: unknown) => error instanceof TRPCClientError && error.data?.code === "NOT_FOUND";

const opts = {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.error("Caught Error");
        console.error(error);
        console.error(errorInfo);
        if (dsn) {
            Sentry.captureException(error);
        }
    }),
    onCaughtError: (error: any, errorInfo: any) => {
        console.warn("Caught Error");
        console.warn(error);
        console.warn(errorInfo);
        if (dsn && !isHandledNotFound(error)) {
            Sentry.captureException(error);
        }
    },
    onRecoverableError: (error: any, errorInfo: any) => {
        console.warn("Recoverable Error");
        console.warn(error);
        console.warn(errorInfo);
        if (dsn) {
            Sentry.captureException(error);
        }
    },
    // don't log any errors that are handled
    // Callback called when React catches an error in an ErrorBoundary.
    //   onCaughtError: Sentry.reactErrorHandler(),
    //   // Callback called when React automatically recovers from errors.
    //   onRecoverableError: Sentry.reactErrorHandler(),
};

createRoot(document.getElementById("root")!, opts).render(
    <>
        <CookieConsentBanner />
        <App />
        <Portal.Root>
            <Toaster
                icons={{
                    error: <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />,
                }}
            />
        </Portal.Root>
    </>
);
